import type { PublicHeroAction } from './PublicMarketing';

export type PublicGuideSection = {
  title: string;
  body: readonly string[];
};

export type PublicGuidePageContent = {
  slug: string;
  path: string;
  title: string;
  navLabel: string;
  description: string;
  imageAlt: string;
  eyebrow: string;
  hero: {
    title: string;
    summary: string;
    primaryAction: PublicHeroAction;
    secondaryAction?: PublicHeroAction;
  };
  sections: readonly PublicGuideSection[];
  checklist: readonly string[];
};

const sharedPrimaryAction = {
  href: '/sign-up',
  label: 'Create Free Account',
  variant: 'primary',
} as const satisfies PublicHeroAction;

const sharedSecondaryAction = {
  href: '/preview',
  label: 'Open Preview',
  variant: 'secondary',
} as const satisfies PublicHeroAction;

export const publicGuidePages = [
  {
    slug: 'upload-your-first-test-results',
    path: '/guides/upload-your-first-test-results',
    title: 'Upload Your First Test Results | FlowQA Guides',
    navLabel: 'Upload Your First Test Results',
    description:
      'A practical guide to uploading your first test results into FlowQA and checking whether the imported run gives your QA team useful reporting evidence.',
    imageAlt: 'FlowQA guide for uploading first test results',
    eyebrow: 'Guide',
    hero: {
      title: 'Upload your first test results',
      summary:
        'Start with one recent test result file, check the imported run, and use the first upload to decide whether FlowQA fits your QA reporting workflow.',
      primaryAction: sharedPrimaryAction,
      secondaryAction: sharedSecondaryAction,
    },
    sections: [
      {
        title: 'Pick a recent result file',
        body: [
          'Use a file from a real run rather than a perfect sample. A recent CI or local test result is more useful because it shows whether FlowQA understands the kind of evidence your team already produces.',
          'JUnit XML is usually the simplest first upload. If your team works from spreadsheets, start with one small CSV or XLSX file whose columns are easy to recognise.',
        ],
      },
      {
        title: 'Upload before you optimise',
        body: [
          'The first upload is about learning, not polishing. Import the file, then check whether FlowQA can identify the run, suites, tests, statuses, durations, and failures you expected.',
          'If the import succeeds but the report feels thin, the next useful step is usually adding another run from the same project so the history has something to compare against.',
        ],
      },
      {
        title: 'Use the first run as a quality snapshot',
        body: [
          'After the upload, look for three things: the overall status, the failure list, and whether the suite/test names are meaningful enough for someone else to review.',
          'A good first result should help you answer one simple question: could this replace a screenshot, raw CI log, or spreadsheet summary in a QA update?',
        ],
      },
    ],
    checklist: [
      'Use a real recent file',
      'Check run, suite, and test names',
      'Confirm failures are visible',
      'Upload a second run when you want history',
    ],
  },
  {
    slug: 'junit-xml-test-results',
    path: '/guides/junit-xml-test-results',
    title: 'JUnit XML Test Results | FlowQA Guides',
    navLabel: 'JUnit XML Test Results',
    description:
      'Learn what JUnit XML test result files contain and why QA teams use them for CI test history, failure reporting, and FlowQA imports.',
    imageAlt: 'FlowQA guide to JUnit XML test results',
    eyebrow: 'Guide',
    hero: {
      title: 'What is in a JUnit XML test result?',
      summary:
        'JUnit XML is a common exchange format for test outcomes. FlowQA uses it to turn raw CI output into reviewable runs, suites, tests, failures, and timing data.',
      primaryAction: sharedPrimaryAction,
      secondaryAction: sharedSecondaryAction,
    },
    sections: [
      {
        title: 'JUnit XML is a test-report format',
        body: [
          'Despite the name, JUnit XML is used far beyond Java. Many test tools can export it because CI systems and reporting tools understand the same basic structure.',
          'A typical file contains test suites, test cases, statuses, durations, and failure messages. The exact shape varies by tool, which is why result importers need to be tolerant.',
        ],
      },
      {
        title: 'The useful fields are simple',
        body: [
          'For a QA lead, the most important fields are usually test name, suite name, status, duration, and failure detail. Those fields are enough to build a practical first report.',
          'Metadata such as branch, commit, environment, and run name adds context, especially when the same project produces results from multiple pipelines.',
        ],
      },
      {
        title: 'History makes the format more valuable',
        body: [
          'One XML file tells you what happened in one run. Several files over time help you see repeated failures, slow tests, and whether a suite is becoming healthier or noisier.',
          'That is the point of importing results into FlowQA instead of only keeping them as CI artifacts.',
        ],
      },
    ],
    checklist: [
      'Suite names should be recognisable',
      'Failed tests should include useful messages',
      'Durations help spot slow areas',
      'Repeated imports create trend history',
    ],
  },
  {
    slug: 'share-test-results-with-your-team',
    path: '/guides/share-test-results-with-your-team',
    title: 'Share Test Results With Your Team | FlowQA Guides',
    navLabel: 'Share Test Results With Your Team',
    description:
      'How QA leads can share test result history with engineering managers, developers, and stakeholders without forwarding raw CI logs or spreadsheets.',
    imageAlt: 'FlowQA guide for sharing test results with a team',
    eyebrow: 'Guide',
    hero: {
      title: 'Share test results without forwarding raw logs',
      summary:
        'A useful test report gives teammates enough context to understand quality status without asking QA to rebuild the evidence trail every time.',
      primaryAction: sharedPrimaryAction,
      secondaryAction: sharedSecondaryAction,
    },
    sections: [
      {
        title: 'Start with the person who acts on the result',
        body: [
          'For many small teams, the first useful audience is the engineering manager or developer who decides whether a failure needs fixing now.',
          'Invite people once there is at least one real upload to inspect. The conversation is easier when the report already shows recognisable tests and failures.',
        ],
      },
      {
        title: 'Share history, not just status',
        body: [
          'A red or green result is useful, but history makes the report more credible. When a teammate can see whether a failure is new or recurring, triage becomes less subjective.',
          'The second upload is often the moment FlowQA becomes more useful, because the report can start showing movement rather than a single snapshot.',
        ],
      },
      {
        title: 'Keep the report attached to the evidence',
        body: [
          'The best QA update connects summary and detail. Use the high-level outcome for the conversation, then drill into the exact run, suite, or failed test when someone asks why.',
          'This keeps reporting lightweight while still giving engineers the evidence they need.',
        ],
      },
    ],
    checklist: [
      'Invite teammates after a useful first upload',
      'Show whether failures are new or repeated',
      'Keep summary and evidence linked',
      'Use one workspace for shared review',
    ],
  },
  {
    slug: 'track-flaky-and-repeated-failures',
    path: '/guides/track-flaky-and-repeated-failures',
    title: 'Track Flaky And Repeated Failures | FlowQA Guides',
    navLabel: 'Track Flaky And Repeated Failures',
    description:
      'A QA guide to spotting repeated test failures, flaky patterns, and noisy test results using historical run data instead of one-off CI output.',
    imageAlt: 'FlowQA guide for tracking flaky and repeated failures',
    eyebrow: 'Guide',
    hero: {
      title: 'Track repeated failures before they become background noise',
      summary:
        'A single failed run tells you something broke. A run history helps you see whether the same failure keeps returning, moving, or hiding inside noisy output.',
      primaryAction: sharedPrimaryAction,
      secondaryAction: sharedSecondaryAction,
    },
    sections: [
      {
        title: 'Repeated failures need a timeline',
        body: [
          'A failure that appears once may be urgent or harmless. A failure that appears across multiple runs is a pattern, and patterns are much easier to discuss with engineering.',
          'FlowQA is most useful when you import enough runs to compare the same tests over time.',
        ],
      },
      {
        title: 'Flaky tests need context',
        body: [
          'A flaky test often passes and fails without an obvious code change. The practical question is not only whether it failed, but how often and where it appears.',
          'Look at status movement, durations, and repeated names. Those signals help separate a real product regression from unstable test infrastructure.',
        ],
      },
      {
        title: 'Make recurring issues visible',
        body: [
          'Repeated failures become easier to ignore when they live only in CI logs. Putting them into a shared report gives QA a calmer way to show why a test deserves attention.',
          'The goal is not to blame a team. It is to make the recurring quality cost visible enough to prioritise.',
        ],
      },
    ],
    checklist: [
      'Import several runs from the same project',
      'Compare failed test names over time',
      'Watch duration changes',
      'Use history to support triage',
    ],
  },
  {
    slug: 'qa-reporting-for-engineering-managers',
    path: '/guides/qa-reporting-for-engineering-managers',
    title: 'QA Reporting For Engineering Managers | FlowQA Guides',
    navLabel: 'QA Reporting For Engineering Managers',
    description:
      'A practical guide for QA leads who need to explain test quality, recurring failures, and release risk to engineering managers.',
    imageAlt: 'FlowQA guide for QA reporting to engineering managers',
    eyebrow: 'Guide',
    hero: {
      title: 'QA reporting for engineering managers',
      summary:
        'The best QA reports help an engineering manager decide what needs attention, what changed, and where to inspect the evidence.',
      primaryAction: sharedPrimaryAction,
      secondaryAction: sharedSecondaryAction,
    },
    sections: [
      {
        title: 'Lead with the decision',
        body: [
          'Engineering managers rarely need every test detail first. They need to know whether quality is stable enough, where risk is concentrated, and whether failures are new or recurring.',
          'Use FlowQA to keep the summary connected to the run evidence so the conversation can go deeper only when needed.',
        ],
      },
      {
        title: 'Show movement over time',
        body: [
          'A useful report compares recent results, not only the latest run. Movement helps an EM understand whether the team is improving, regressing, or fighting the same problems repeatedly.',
          'Pass rate, failure recurrence, duration changes, and suite status are usually better signals than raw test counts alone.',
        ],
      },
      {
        title: 'Make QA visible without extra ceremony',
        body: [
          'For a QA lead trying to make an impression, the report should feel prepared without becoming another weekly manual chore.',
          'The goal is a lightweight habit: upload or sync results, review the changes, and share the evidence-backed quality story.',
        ],
      },
    ],
    checklist: [
      'Summarise the quality decision',
      'Separate new and recurring failures',
      'Use trends rather than one-off counts',
      'Keep evidence close to the summary',
    ],
  },
] as const satisfies readonly PublicGuidePageContent[];
